import { CommonButton, ErrorPage, IconButton, InfoCardInner, LoadingPage, Modal, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TCuratedPackageType, useQueryOneGiftPackage } from '../../entities/giftPackages/queries';
import { getReadableGiftPackageType } from '../../entities/giftPackages/shared';
import { TProduct } from '../../generated/graphql';
import { AddProductToPackageModal } from './AddProductToPackageModal';
import styles from './GiftPackageDetails.module.scss';
import { ProductModalDetails } from './ProductModalDetails';

export const GiftPackageDetails = () => {
  const { packageId = '0' } = useParams();
  const navigate = useNavigate();

  const { data: giftPackage, loading: giftPackageLoading, error: giftPackageError } = useQueryOneGiftPackage({ id: packageId });

  const closeModal = useCallback(() => {
    navigate(generatePath('../'));
  }, [navigate]);

  //console.log('giftPackage', giftPackage);

  const curatedPackage: TCuratedPackageType = useMemo(
    () => ({
      id: giftPackage.id as string,
      _version: giftPackage._version as number,
      products: giftPackage.products as string[]
    }),
    [giftPackage]
  );

  const [addProductModalOpen, setAddProductModalOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(false);

  const closeAddProductModal = useCallback(() => {
    setAddProductModalOpen(false);
    setIsEditing(false);
  }, []);

  const topComponent = useMemo(() => {
    if (giftPackage != null)
      return (
        <div className={styles.topComponent}>
          <div className={styles.topTitleContainer}>
            <h2 className={styles.topTitle}>{giftPackage.name ?? '-'}</h2>
            {!isEditing && (
              <div className={styles.topIcons}>
                <Tooltip message='Edit products'>
                  <IconButton disabled={false} icon={'edit'} onClick={() => setIsEditing(true)} variant='outlined' />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      );
  }, [giftPackage, isEditing]);

  const bottomComponent = useCallback(() => {
    if (!giftPackage) return <></>;
    return (
      <div className={styles.topComponent}>
        <CommonButton className={clsx(styles.addBtn)} leadingIcon='close' label='Cancel' onClick={() => setIsEditing(false)} variant='outlined' />
        <CommonButton className={clsx(styles.addBtn)} leadingIcon='add' label='Add Product to this Gift Package' onClick={() => setAddProductModalOpen(true)} variant='filled' />
      </div>
    );
  }, [giftPackage]);

  if (giftPackageLoading)
    return (
      <Modal closeModal={closeModal} headline={`Details Gift Package/Kit`}>
        <LoadingPage />
      </Modal>
    );

  if (giftPackageError) {
    console.error('query Apollo ERROR:', giftPackageError);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (giftPackageError.message) toast.error(giftPackageError.message);
    if (!giftPackage)
      return (
        <Modal closeModal={closeModal} headline={`Details Gift Package/Kit`}>
          <ErrorPage error={giftPackageError} />
        </Modal>
      );
  }

  return (
    <>
      <Modal closeModal={closeModal} headline={'Gift Package/Kit Details'} size='large' customButton={bottomComponent} showCustomButton={isEditing}>
        <InfoCardInner
          className={styles.infoCardInnerContainer}
          data={giftPackage}
          sections={[
            {
              topComponent: () => topComponent,
              columns: [
                {
                  label: 'Gift Package Type',
                  render: (data) => <span> {getReadableGiftPackageType(data?.type as string)}</span>
                },
                {
                  label: 'Company',
                  render: () => <span> {giftPackage.company?.name || '-'}</span>
                }
              ]
            }
          ]}
        />
        <div className={styles.productsContainer}>
          {((giftPackage.hydratedProducts as unknown as TProduct[]) || []).map((product) => {
            return <ProductModalDetails key={product.id} product={{ ...product }} isEditing={isEditing} setIsEditing={setIsEditing} giftPackage={curatedPackage} />;
          })}
          {giftPackage.products?.length === 0 && (
            <InfoCardInner
              data={{}}
              className={clsx(styles.emptyProductsCard)}
              sections={[
                {
                  topComponent: () => <span>There are no Products in this Gift Package</span>,
                  columns: []
                }
              ]}
            />
          )}
        </div>
      </Modal>
      <AddProductToPackageModal isOpen={addProductModalOpen} closeModal={closeAddProductModal} giftPackage={curatedPackage} />
    </>
  );
};
